import React, { useState } from 'react';

import {
  WeeklyPlanRenderer,
  MOCK_RUNNING_PLAN,
  MOCK_CYCLING_PLAN,
} from '../components/WeeklyPlanRenderer';

// Hits the v2 endpoint. Prefer REACT_APP_API_URL (set via .env.development
// to http://localhost:8000). Falls back to localhost so a clean dev clone
// "just works" against a local backend.
const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Demo page for the JSON plan renderer. Supports three data sources:
//   • Mock (running)   — fixed sample, useful for offline UI work
//   • Mock (cycling)   — same, for the cycling layout
//   • API (live)       — POST /weekly-plan/generate-v2 to the configured backend
//
// In API mode the page prompts for a user_id (Firebase UID) and a week
// type, then renders the validated plan it gets back. Validation errors
// from the backend (502 body) are surfaced verbatim so we can debug.
export default function WeeklyPlanRendererDemo({ onBack }) {
  const [source, setSource] = useState('mock-running');
  const [userId, setUserId] = useState('');
  const [weekType, setWeekType] = useState('current');
  const [apiPlan, setApiPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchFromApi = async () => {
    if (!userId.trim()) {
      setError({ message: 'נא להזין user_id' });
      return;
    }
    setLoading(true);
    setError(null);
    setApiPlan(null);
    try {
      const url = `${API_BASE_URL}/weekly-plan/generate-v2?user_id=${encodeURIComponent(userId.trim())}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ week_type: weekType, sport_type: 'running' }),
      });
      const body = await res.json();
      if (!res.ok) {
        // Backend returns 502 with detail.errors on validation failure.
        setError({
          status: res.status,
          message: body?.detail?.message || body?.detail || body?.message || 'Backend error',
          errors: body?.detail?.errors,
          raw: body?.detail?.raw_snippet,
        });
        return;
      }
      if (body?.plan) {
        setApiPlan(body.plan);
      } else {
        setError({ message: 'התשובה לא כללה plan', body });
      }
    } catch (e) {
      setError({ message: `Network error: ${e.message}` });
    } finally {
      setLoading(false);
    }
  };

  const planToRender =
    source === 'api'
      ? apiPlan
      : source === 'mock-cycling'
        ? MOCK_CYCLING_PLAN
        : MOCK_RUNNING_PLAN;

  return (
    <div dir="rtl" className="min-h-screen bg-[#0f172a]">
      <DemoToolbar
        source={source}
        onSourceChange={setSource}
        userId={userId}
        onUserIdChange={setUserId}
        weekType={weekType}
        onWeekTypeChange={setWeekType}
        onFetch={fetchFromApi}
        loading={loading}
        onBack={onBack}
        apiBase={API_BASE_URL}
      />

      {error && <ErrorPanel error={error} />}

      {loading && (
        <div className="max-w-2xl mx-auto p-6 text-center text-gray-400">
          <div className="animate-pulse">⏳ קורא לAPI... זה יכול לקחת 10-30 שניות (כולל retry במידת הצורך)</div>
        </div>
      )}

      {planToRender && !loading && <WeeklyPlanRenderer plan={planToRender} />}

      {source === 'api' && !apiPlan && !loading && !error && (
        <div className="max-w-2xl mx-auto p-6 text-center text-gray-500 text-sm">
          הזן user_id ולחץ "טען מהAPI" כדי לראות תכנית אמיתית.
        </div>
      )}
    </div>
  );
}

// ── Toolbar ──────────────────────────────────────────────────────────

function DemoToolbar({
  source,
  onSourceChange,
  userId,
  onUserIdChange,
  weekType,
  onWeekTypeChange,
  onFetch,
  loading,
  onBack,
  apiBase,
}) {
  return (
    <div className="px-3 pt-3 max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-3">
        {process.env.NODE_ENV === 'development' ? (
          <div className="text-gray-600 text-xs font-bold">PREVIEW</div>
        ) : <div />}
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="text-gray-400 hover:text-white text-sm"
          >
            ← חזרה
          </button>
        )}
      </div>

      {/* Source selector */}
      <div className="flex gap-2 mb-3 flex-wrap">
        <SourceButton
          active={source === 'mock-running'}
          onClick={() => onSourceChange('mock-running')}
          activeColor="cyan"
        >
          🏃 Mock ריצה
        </SourceButton>
        <SourceButton
          active={source === 'mock-cycling'}
          onClick={() => onSourceChange('mock-cycling')}
          activeColor="orange"
        >
          🚴 Mock רכיבה
        </SourceButton>
        <SourceButton
          active={source === 'api'}
          onClick={() => onSourceChange('api')}
          activeColor="emerald"
        >
          🔌 חיבור לAPI
        </SourceButton>
      </div>

      {/* API form — visible only when API source is selected */}
      {source === 'api' && (
        <div className="mb-3 p-3 rounded-xl bg-[#1e293b] border border-white/10">
          <div className="text-[11px] text-gray-500 mb-2">
            POST {apiBase}/weekly-plan/generate-v2
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              value={userId}
              onChange={(e) => onUserIdChange(e.target.value)}
              placeholder="user_id (Firebase UID)"
              className="flex-1 px-3 py-2 rounded-lg bg-[#0f172a] border border-white/10 text-white text-sm font-mono placeholder-gray-600 focus:outline-none focus:border-cyan-500/50"
            />
            <select
              value={weekType}
              onChange={(e) => onWeekTypeChange(e.target.value)}
              className="px-3 py-2 rounded-lg bg-[#0f172a] border border-white/10 text-white text-sm"
            >
              <option value="current">השבוע</option>
              <option value="next">השבוע הבא</option>
            </select>
            <button
              type="button"
              onClick={onFetch}
              disabled={loading || !userId.trim()}
              className="px-4 py-2 rounded-lg bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 font-bold text-sm hover:bg-emerald-500/30 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {loading ? '⏳' : 'טען מהAPI'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SourceButton({ active, onClick, children, activeColor = 'cyan' }) {
  const activeClass = {
    cyan:    'bg-cyan-500/20 border-cyan-500/60 text-cyan-200',
    orange:  'bg-orange-500/20 border-orange-500/60 text-orange-200',
    emerald: 'bg-emerald-500/20 border-emerald-500/60 text-emerald-200',
  }[activeColor] || 'bg-cyan-500/20 border-cyan-500/60 text-cyan-200';

  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-lg text-sm border transition ${
        active
          ? activeClass
          : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10'
      }`}
    >
      {children}
    </button>
  );
}

// ── Error panel — surfaces backend 502 details ───────────────────────

function ErrorPanel({ error }) {
  return (
    <div className="max-w-2xl mx-auto px-3 mt-2">
      <div className="rounded-xl bg-red-500/10 border border-red-500/30 p-3">
        <div className="text-red-300 text-sm font-bold mb-1">
          ❌ {error.status ? `${error.status} · ` : ''}{error.message || 'שגיאה'}
        </div>
        {error.errors && Array.isArray(error.errors) && error.errors.length > 0 && (
          <ul className="text-red-200 text-xs mt-2 space-y-1">
            {error.errors.slice(0, 10).map((e, i) => {
              const loc = Array.isArray(e.loc) ? e.loc.join('.') : '';
              return (
                <li key={i} className="font-mono">
                  <span className="text-red-400">{loc || '<root>'}: </span>
                  <span>{e.msg || JSON.stringify(e)}</span>
                </li>
              );
            })}
          </ul>
        )}
        {error.raw && (
          <details className="mt-2 text-xs">
            <summary className="text-red-400 cursor-pointer">raw_snippet</summary>
            <pre className="mt-1 text-red-300/80 whitespace-pre-wrap break-all">{error.raw}</pre>
          </details>
        )}
      </div>
    </div>
  );
}
